import { Component, Element, Fragment, h, Host, Prop, State, Watch } from '@stencil/core';
import type { ChartConfiguration, DoughnutControllerChartOptions, Plugin } from 'chart.js';
import DoughnutLabel from 'chartjs-plugin-doughnutlabel-rebourne';

import type { ChartType } from '../../../services/libraries';
import { ChartJsService } from '../../../services/libraries';
import { getCSSVariableValue, isNumber, parsePropToArray } from '../../../services/utils';

let donutChartId = 0;

/**
 * A Donut chart shows a total number and how categories represent part of a whole.
 *
 * @part canvas-outline - Div container that holds the chart canvas 2d
 * @part canvas - Canvas element that renders the chart
 * @part legend-outline - Div container that holds the legend
 * @part legend - Div element for the legend
 */
@Component({
  tag: 'pk-donut-chart',
  styleUrl: './scss/pk-donut-chart.scss',
  shadow: true,
})
export class PkDonutChart {
  // Own Properties
  // ====================

  private canvasElemRef: HTMLCanvasElement;
  private donutCharId = `donut-chart-${++donutChartId}`;
  private initialCutout = '94%';

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkDonutChartElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() chart: ChartJsService;
  @State() chartConfig: ChartConfiguration & { options: Partial<DoughnutControllerChartOptions> };
  @State() chartSeries: number[];
  @State() chartLabels: string[];
  @State() chartColors: string[];

  // Public Property API
  // ========================

  /** Specify the colors for secah serie value of the chart. The colors property expects a single array. */
  @Prop({ reflect: true }) colors: string | string[];

  /** The portion of the chart that is cut out of the middle. String ending with '%' means percentage, number means pixels. */
  @Prop({ reflect: true }) cutout: string | number;

  /** It will set the height of the SVG chart (only the circle) */
  @Prop({ reflect: true }) height: number = 150;

  /** If true, the legend won't be displayed.
   * If you really need to hide the legend, is recomended to use the default legend position. */
  @Prop({ reflect: true }) hideLegend: boolean = false;

  /** Specify the labels for each serie value of the chart. The labels property expects a single array. */
  @Prop({ reflect: true }) labels: string | string[];

  /** Specify where the legend should be positioned, at the right (default) or bottom side of the chart. */
  @Prop({ reflect: true }) legendPosition: 'default' | 'bottom' = 'default';

  /** Primary text label inside the chart circle. */
  @Prop({ reflect: true }) primaryText: string = 'Total';

  /** Secondary text label inside the chart circle. Use it only when you need multline label inside the donut chart */
  @Prop({ reflect: true }) secondaryText: string;

  /** Specify the series values for the chart. The series property expects a single array of numbers. */
  @Prop({ reflect: true }) series: string | number[];

  /** @deprecated Color to fill the border of the series */
  @Prop({ reflect: true }) strokeColor: string;

  /** If set, it will override the sum value of all the series data. */
  @Prop({ reflect: true }) totalValue: string;

  /** It will set the width of the SVG chart (only the circle) */
  @Prop({ reflect: true }) width: number = 150;

  // Prop lifecycle events
  // =======================

  @Watch('cutout')
  updateCutout() {
    if (!this.chart) return;

    // If not cutout has been provided
    // Increase the inner circle if the size is bigger so the stroke weight is not affected
    const initial = this.width > 200 ? '96%' : this.initialCutout;
    const cutout = !this.cutout ? initial : this.cutout;
    this.chart.updateDonutChartCutout(cutout);
  }

  @Watch('colors')
  updateColors() {
    this.chartColors = this.parseColors();
    this.chartConfig.data.datasets[0].backgroundColor = this.chartColors;
  }

  @Watch('labels')
  updateLabels() {
    this.chartLabels = parsePropToArray(this.labels);
    this.chartConfig.data.labels = this.chartLabels;
  }

  @Watch('series')
  updateSeries() {
    this.chartSeries = parsePropToArray(this.series);
    this.chartConfig.data.datasets[0].data = this.chartSeries;
  }

  @Watch('cutout')
  @Watch('colors')
  @Watch('labels')
  @Watch('series')
  onPropChange() {
    if (!this.chart) return;

    this.chart.update(this.chartConfig);
  }

  @Watch('height')
  @Watch('width')
  onSizeChange() {
    this.updateCutout();
  }

  @Watch('primaryText')
  @Watch('secondaryText')
  @Watch('totalValue')
  updateChartTotalLabels() {
    if (!this.chart) return;

    this.chart.destroy();
    this.initChart();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.chartSeries = parsePropToArray(this.series);
    this.chartLabels = parsePropToArray(this.labels);
    this.chartColors = this.parseColors();
  }

  componentDidLoad() {
    this.initChart();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private beforeChartDraw = (chart: ChartType, host: Element) => {
    const data = this.chartSeries;
    const isAllZero = data.length === 0 || data.every((value) => value === 0);
    if (!isAllZero) return;

    const { ctx, width, height } = chart;
    const radius = Math.min(width, height) / 2;
    const lineWidth = this.computeLineWidth(radius);

    ctx.restore();

    ctx.strokeStyle = getCSSVariableValue('pk-ui-01', host);
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, radius - lineWidth / 2, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.stroke();

    ctx.save();
  };

  private computeLineWidth = (radius: number): number => {
    if (!this.cutout) return 4;
    if (isNumber(this.cutout)) return radius - this.cutout;

    const cutout = parseFloat(this.cutout) / 100;
    return radius - radius * cutout;
  };

  private setChartConfig = () => {
    const host = this.el.shadowRoot.host;
    if (!host) return;

    this.chartConfig = {
      type: 'doughnut',
      data: {
        labels: this.chartLabels,
        datasets: [
          {
            data: this.chartSeries,
            backgroundColor: this.chartColors,
            borderRadius: 9999,
            borderWidth: 0,
            hoverBorderWidth: 0,
            spacing: 3,
          },
        ],
      },
      plugins: [
        DoughnutLabel as unknown as Plugin,
        {
          id: 'beforeDraw',
          beforeDraw: (chart) => this.beforeChartDraw(chart, host),
        },
      ],
      options: {
        cutout: this.cutout || this.initialCutout,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
          doughnutlabel: {
            labels: [
              {
                text: this.getTotalValue(),
                font: {
                  size: parseInt(getCSSVariableValue('donut-chart--total-value-font-size', host), 10),
                  family: getCSSVariableValue('donut-chart--font-family-bold', host),
                },
                color: getCSSVariableValue('donut-chart--total-value-color', host),
              },
              {
                text: this.primaryText ?? '',
                font: {
                  size: !this.primaryText
                    ? '0'
                    : parseInt(getCSSVariableValue('donut-chart--label-font-size', host), 10),
                  family: getCSSVariableValue('donut-chart--font-family', host),
                },
                color: getCSSVariableValue('donut-chart--label-color', host),
              },
              {
                text: this.secondaryText ?? '',
                font: {
                  size: !this.secondaryText
                    ? '0'
                    : parseInt(getCSSVariableValue('donut-chart--label-font-size', host), 10),
                  family: getCSSVariableValue('donut-chart--font-family', host),
                },
                color: getCSSVariableValue('donut-chart--label-color', host),
              },
            ],
          },
        },
      } as Partial<DoughnutControllerChartOptions>,
    };
  };

  private initChart = () => {
    this.setChartConfig();
    this.chart = new ChartJsService(this.canvasElemRef, this.chartConfig as ChartConfiguration);

    if (this.width > 200) {
      this.onSizeChange();
    }
  };

  private parseColors = (): string[] => {
    const host = this.el.shadowRoot.host;
    const colors: string[] = parsePropToArray(this.colors);
    // Since chart.js uses canvas to render the chart,
    // we need to pass the hexadecimal color value instead because the draw inside the canvas
    // have no access to the defined Panenka CSS custom properties.
    return colors.map((color: string) => getCSSVariableValue(`pk-${color}`, host));
  };

  private getTotalValue = () => {
    if (isNumber(this.totalValue)) return +this.totalValue;
    return this.chartSeries.reduce((a: number, b: number) => a + b, 0);
  };

  private renderLegendDefault = () => {
    const seriesData = this.chartConfig?.data?.datasets[0].data;
    return (
      <Fragment>
        <div class="donut-chart-legend--series">
          {seriesData?.map((value: number, i: number) => (
            <div class={{ series: true, 'is-last': i === seriesData.length - 1 }} key={`serie-${i + 1}`}>
              <span>{value}</span>
            </div>
          ))}
        </div>
        <div class="donut-chart-legend--bullets">
          {seriesData?.map((_: number, i: number) => (
            <div class={{ bullet: true, 'is-last': i === seriesData.length - 1 }} key={`bullet-${i + 1}`}>
              <span
                class={`color-${i}`}
                style={{ '--pk-chart-legend-bullet--color': `var(--pk-${parsePropToArray(this.colors)[i]})` }}
              />
            </div>
          ))}
        </div>
        <div class="donut-chart-legend--labels">
          {seriesData?.map((_: number, i: number) => (
            <div class={{ label: true, 'is-last': i === seriesData.length - 1 }} key={`label-${i + 1}`}>
              <span>{this.chartLabels[i]}</span>
            </div>
          ))}
        </div>
      </Fragment>
    );
  };

  private renderLegendBottom = () => {
    const seriesData = this.chartConfig?.data?.datasets[0].data;
    return (
      <Fragment>
        {seriesData?.map((value: number, index: number) => (
          <div class="mr-xs flex w-[25%] flex-col justify-start last:mr-0" key={`legend-${this.chartLabels[index]}`}>
            <div class="flex flex-row items-center justify-center">
              <div class={`bullet legend--${this.legendPosition} !m-0 !p-0`}>
                <span
                  class={`color-${index} !mt-0`}
                  style={{ '--pk-chart-legend-bullet--color': `var(--pk-${parsePropToArray(this.colors)[index]})` }}
                />
              </div>
              <div class={`series legend--${this.legendPosition} ml-xs !p-0`}>
                <span>{value}</span>
              </div>
            </div>
            <div class={`label legend--${this.legendPosition} justify-center`}>
              <span class="text-ellipsis text-center leading-s">{this.chartLabels[index]}</span>
            </div>
          </div>
        ))}
      </Fragment>
    );
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.height && { '--donut-chart--height': `${this.height}px` }),
      ...(this.width && { '--donut-chart--width': `${this.width}px` }),
    };

    return (
      <Host class={`legend--${this.legendPosition}`} style={style}>
        <div class={`donut-chart flex legend--${this.legendPosition}`}>
          <div
            class="donut-chart__canvas--outline relative h-[--donut-chart--height] w-[--donut-chart--width]"
            part="canvas-outline"
          >
            <canvas
              id={this.donutCharId}
              ref={(canvas: HTMLCanvasElement) => (this.canvasElemRef = canvas)}
              part="canvas"
            />
          </div>
          {!this.hideLegend && (
            <div class={`donut-chart-legend--outline legend--${this.legendPosition}`} part="legend-outline">
              <div class={`donut-chart-legend legend--${this.legendPosition}`} part="legend">
                {/* Legend: DEFAULT position  */}
                {this.legendPosition !== 'bottom' && this.renderLegendDefault()}
                {/* Legend: BOTTOM position  */}
                {this.legendPosition === 'bottom' && this.renderLegendBottom()}
              </div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
